import { useState } from "react";
import {
  openMaterialUsageFile,
  rematchMaterialUsageLine,
  resendMaterialUsage,
  type MaterialUsageLine,
  type MaterialUsagePreview,
} from "@/lib/service/materialUsageClient";
import { ghostBtnClass, inputClass } from "./serviceShared";

export function ServiceMaterialUsageAfterSubmit(props: {
  reportId: string;
  preview: MaterialUsagePreview;
  canRematch: boolean;
  canCorrect: boolean;
  canResend: boolean;
  hasFile: boolean;
  mailStatus: string | null;
  busy: boolean;
  onCorrect: () => void;
  onMailStatus: (status: string) => void;
  onRematched: (index: number, materialId: string | null) => void;
  onError: (message: string) => void;
}) {
  const [pendingId, setPendingId] = useState<string | null>(null);

  async function onPick(index: number, line: MaterialUsageLine, materialId: string) {
    if (!line.id) return;
    setPendingId(line.id);
    const result = await rematchMaterialUsageLine({
      report_id: props.reportId,
      line_id: line.id,
      material_id: materialId || null,
    });
    setPendingId(null);
    if (!result.ok) {
      props.onError(result.error);
      return;
    }
    props.onRematched(index, materialId || null);
  }

  async function openFile() {
    const result = await openMaterialUsageFile(props.reportId);
    if (!result.ok) {
      props.onError(result.error);
      return;
    }
    window.open(result.data.url, "_blank", "noopener,noreferrer");
  }

  async function resend() {
    const result = await resendMaterialUsage(props.reportId);
    if (!result.ok) {
      props.onError(result.error);
      return;
    }
    props.onMailStatus(result.data.mail_status);
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-700 dark:text-zinc-300">
        Submitted. Stock is drawn and this report stays closed.
        {props.mailStatus ? ` Mail: ${props.mailStatus}.` : ""}
      </p>
      <div className="flex flex-wrap gap-2">
        {props.hasFile ? (
          <button type="button" className={ghostBtnClass} onClick={() => void openFile()}>
            Original report
          </button>
        ) : (
          <p className="text-sm text-gray-600 dark:text-zinc-400">The original file was not stored.</p>
        )}
        {props.canResend && props.hasFile ? (
          <button type="button" className={ghostBtnClass} disabled={props.busy} onClick={() => void resend()}>
            Resend email
          </button>
        ) : null}
      </div>
      {props.canCorrect ? (
        <button type="button" className={ghostBtnClass} disabled={props.busy} onClick={props.onCorrect}>
          Correct report
        </button>
      ) : null}
      {props.canRematch ? (
        <ul className="space-y-2">
          {props.preview.lines.map((line, index) => (
            <li key={line.id ?? index} className="text-sm text-gray-800 dark:text-zinc-200">
              <span>
                {line.quantity} {line.spoken_phrase}
              </span>
              <select
                className={`${inputClass} mt-1`}
                value={line.material_id ?? ""}
                disabled={pendingId === line.id}
                onChange={(event) => void onPick(index, line, event.target.value)}
              >
                <option value="">Unresolved</option>
                {props.preview.catalog.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
